<script setup lang="ts">
//#region import
import numeral from 'numeral';
import { computed, h, nextTick, ref, resolveComponent } from 'vue';
import { isEmpty, merge, omit } from 'lodash-es';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTypedStore } from '@/store';
import { useSessionAction } from '@/composables/useSessionAction';
import updateUrl from '@/service/updateUrl';
import { useApi } from '@/api';
import { useTablePaginator, useTableSort, useTableSearch } from '@/composables/useTableLS';
import { usePopupManager } from '@/components/PopupManager.vue';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import type {
  GetControlTowerPaymentMethodsResponse,
  GetControlTowerPaymentMethodsData,
} from '@/types/openapi-ts';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { SettingsPageQuery } from '@/types/core';
// import type { UiPagination } from '@/types/table';
//#endregion


//#region types
type PaymentMethodsQuery = SettingsPageQuery<GetControlTowerPaymentMethodsData['query']>;
//#endregion

//#region const
const sizes = {
  1080: 20,
  1440: 30,
};
const LS_PATH = 'PaymentMethods/List';
const fields: UiTableNewField<GetControlTowerPaymentMethodsResponse['payload'][number]>[] = [
  {
    name: 'id',
    width: '250',
    align: 'left',
  },
  {
    name: 'status',
    minWidth: '80',
    type: 'status',
    statusText: (value, row) => t(`settings.paymentMethods.statuses.${row?.is_disabled ? 'disabled' : 'active'}`),
    status: (value, row) => (row?.is_disabled ? 'negative' : 'positive'),
  },
  {
    name: 'visibility',
    width: '90',
    align: 'center',
    headerAlign: 'center',
    actionLib: 'fa',
    controlType: 'icon',
    action: () => {},
    controlColorIco: (row) => (row.hidden ? 'var(--text-negative)' : 'var(--text-positive)'),
    actionName: (row) => (row.hidden ? 'eye-slash' : 'eye'),
  },
  {
    name: 'name',
    minWidth: '264',
    sortable: 'custom',
  },
  {
    name: 'currencies',
    minWidth: '264',
    linkAppend: (row) => renderCurrencies(row, 7),
  },
  {
    name: 'logo',
    minWidth: '120',
    align: 'center',
    headerAlign: 'center',
    computedImg: (row) => row.logo_base64!,
    computedImgNull: () => '',
  },
  {
    name: 'export',
    width: '130',
    align: 'center',
    headerAlign: 'center',
    actionLib: 'fa',
    controlType: 'icon',
    action: () => {},
    controlColorIco: (row) => (!row.export_via_api ? 'var(--text-negative)' : 'var(--text-positive)'),
    actionName: (row) => (row.export_via_api ? 'check' : 'times'),
  },
  {
    name: 'fileAttachments',
    width: '150',
    align: 'center',
    headerAlign: 'center',
    actionLib: 'fa',
    controlType: 'icon',
    class: 'payment-methods__table-file-attachments',
    action: () => {},
    controlColorIco: (row) => (!row.file_attachment_allowed && !row.file_attachment_required
        ? 'var(--text-negative)'
        : 'var(--text-positive)'
    ),
    actionName: renderFileAttachmentsIcon,
    controlColor: (row) => (!row.file_attachment_allowed && !row.file_attachment_required
        ? 'var(--text-negative)'
        : 'var(--text-positive)'
    ),
    computedValue: renderFileAttachments,
  },
  {
    name: 'minPaymentAmount',
    width: '160',
    align: 'right',
    headerAlign: 'right',
    computedValue: (row) => getCurrencyLabel(row.min_payment_amount_currency_code) + numeral(row.min_payment_amount).format('0,0.00'),
  },
  {
    name: 'actions',
    width: '80',
    align: 'center',
    headerAlign: 'center',
    actionLib: 'fa',
    controlType: 'icon-action',
    controlColorIco: () => 'var(--text-primary)',
    action: (row) => handleOpenPaymentMethod(row),
    actionName: 'pen',
    tooltip: 'crm.buttons.edit',
  },
];
//#endregion

//#region composable
const store = useTypedStore();
const api = useApi();
const { t } = useI18n();
const route = useRoute();
const { onSessionUnlock } = useSessionAction();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  lsPath: LS_PATH,
  fields,
});

const { searchQuery, setSearch } = useTableSearch();
const popupManager = usePopupManager();
//#endregion

//#region state
const tableData = ref<GetControlTowerPaymentMethodsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
//#endregion

//#region computed
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
//#endregion

//#region methods
const _query = () => {
  return {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
  } as GetControlTowerPaymentMethodsData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters: PaymentMethodsQuery = omit(_query(), 'offset')!;
  _dataFilters.page = page.value;
  updateUrl.updateGetParams(_dataFilters);
};

const fetchData = () => {
  if(!isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  const options = _query();

  api.getControlPaymentMethods(options).then((res) => {
    tableData.value = res.data.payload;
    count.value = res.data.misc!.count;
  }).finally(() => {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  });
};

const initData = () => {
  const _urlData = updateUrl.getParseParams(route.hash);
  if (!isEmpty(_urlData)) {
    setPage(_urlData);

    delete _urlData.page;
    delete _urlData.limit;
    merge(optionsSort.value, _urlData);

    setSort(_urlData);
    setSearch(_urlData);
  }
  nextTick(() => {
    fetchData();
  });
};

const getCurrencyLabel = (code: string) => {
  const currency = customerCurrencies.value.find(((el) => el.code === code));
  return currency?.symbol || `${code} `;
};

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

const handleOpenPaymentMethod = (row?: GetControlTowerPaymentMethodsResponse['payload'][number]) => {
  popupManager.addPaymentMethod.open({
    data: {
      id: row?.id,
    },
    listeners: {
      success: () => {
        fetchData();
      },
    },
  });
};

function renderFileAttachments(row: GetControlTowerPaymentMethodsResponse['payload'][number]) {
  if (row.file_attachment_allowed) {
    if (row.file_attachment_required) {
      return t('settings.paymentMethods.fileAttachments.required');
    }
    return t('settings.paymentMethods.fileAttachments.allowed');
  }
  return t('settings.paymentMethods.fileAttachments.no');
};

function renderFileAttachmentsIcon(row: GetControlTowerPaymentMethodsResponse['payload'][number]) {
  if (row.file_attachment_allowed) {
    if (row.file_attachment_required) {
      return 'exclamation-circle';
    }
    return 'check';
  }
  return 'times';
};

function renderCurrencies(item: GetControlTowerPaymentMethodsResponse['payload'][number], count: number) {
  if (item.supported_currencies === 'all') {
    return h('div', {class: 't:truncate'}, t('settings.paymentMethods.popup.all'));
  }

  const visibleCurrencies = item.currencies.slice(0, count).map((currency) => currency.code).join(', ');

  if (item.currencies.length <= count) {
    return h('div', {class: 't:truncate'}, visibleCurrencies);
  }

  const Button = resolveComponent('ui-button');
  const UiDropdown = resolveComponent('ui-dropdown');
  const otherCurrencies = item.currencies.slice(count).map((currency) => currency.code);

  const dropdown = h(
    UiDropdown,
    {
      itemsList: otherCurrencies,
    },
    {
      default: () => h(Button,  {size: 'small', class: 't:ml-4'}, {
        default: () => `+${otherCurrencies.length}`,
      }),
    },
  );

  return h('div', { class: 't:flex t:items-center t:w-full' }, [visibleCurrencies, dropdown]);
}
//#endregion

//#region hooks
onSessionUnlock.value = initData;
initData();
//#endregion

</script>

<template>
  <div class="payment-methods">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('settings.paymentMethods.title') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          filled
          icon="plus"
          @click="handleOpenPaymentMethod()"
        >
          {{ $t('settings.paymentMethods.addPaymentMethod') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="settings.paymentMethods"
      @sort="handleSort"
    />
  </div>
</template>

<style lang="scss">
.payment-methods {
  &__table-file-attachments {
    flex-direction: row-reverse;
    span {
      margin-left: 4px;
    }
  }
}
</style>
